import React, { useState } from 'react';
import { X, Plus, Tag, Image } from 'lucide-react';

const MegaMenuColumnEditor = ({ column, onSave, onClose, categories, menuConfig, onBrowseMedia }) => {
  const [editedColumn, setEditedColumn] = useState(column || { 
    type: 'category_list', 
    root_category_id: '',
    selected_subcategories: [],
    custom_items: [],
    labels: [],
    auto_include_subcategories: true  // Default to true
  });

  const handleSave = () => {
    onSave(editedColumn);
    onClose();
  };

  // Flatten category hierarchy to show all categories (root and sub)
  const flattenCategories = (cats, level = 0) => {
    let result = [];
    cats.forEach(cat => {
      result.push({ ...cat, level });
      if (cat.children && cat.children.length > 0) {
        result = result.concat(flattenCategories(cat.children, level + 1));
      }
    });
    return result;
  };

  const allCategories = flattenCategories(categories);

  // Get root category and its children
  const rootCategory = editedColumn.root_category_id 
    ? categories.find(c => c.id === editedColumn.root_category_id)
    : null;

  // Add custom item from another category
  const addCustomItem = () => {
    const newItem = {
      id: Date.now().toString(),
      label: '',
      url: '#'
    };
    setEditedColumn({
      ...editedColumn,
      custom_items: [...(editedColumn.custom_items || []), newItem]
    });
  };

  // Add label/badge - use default color from menu config
  const addLabel = () => {
    const defaultColor = menuConfig?.default_label_color || '#ef4444';
    console.log('Adding label with default color:', defaultColor, 'from menuConfig:', menuConfig);
    const newLabel = {
      id: Date.now().toString(),
      text: 'New',
      color: defaultColor,
      use_default_color: true,
      expires_at: ''
    };
    setEditedColumn({
      ...editedColumn,
      labels: [...(editedColumn.labels || []), newLabel]
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">Edit Column</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Column Type Selector */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">Column Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setEditedColumn({ 
                  type: 'category_list', 
                  root_category_id: '',
                  selected_subcategories: [],
                  custom_items: [],
                  labels: [],
                  auto_include_subcategories: true
                })}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  editedColumn.type === 'category_list'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-semibold mb-1">Category List</div>
                <div className="text-sm text-slate-500">Display subcategories</div>
              </button>
              <button
                type="button"
                onClick={() => setEditedColumn({ type: 'card', title: '', description: '', image: '', button_text: '', button_url: '' })}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  editedColumn.type === 'card'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-semibold mb-1">Custom Card</div>
                <div className="text-sm text-slate-500">Image, text & CTA</div>
              </button>
            </div>
          </div>
          
          {/* Column Background Color */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Column Background Color (Optional)</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={editedColumn.column_bg_color || '#ffffff'}
                onChange={(e) => setEditedColumn({ ...editedColumn, column_bg_color: e.target.value })}
                className="w-20 h-10 border border-slate-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={editedColumn.column_bg_color || ''}
                onChange={(e) => setEditedColumn({ ...editedColumn, column_bg_color: e.target.value })}
                placeholder="#ffffff or transparent"
                className="flex-1 p-2 border border-slate-300 rounded-lg"
              />
              <button
                type="button"
                onClick={() => setEditedColumn({ ...editedColumn, column_bg_color: '' })}
                className="px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg"
              >
                Clear
              </button>
            </div>
            <p className="text-xs text-slate-500">Fill the entire column with this color (spans full height of row)</p>
          </div>

          {/* Category List Config */}
          {editedColumn.type === 'category_list' && (
            <>
              {/* Select Root Category */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">Select Root Category</label>
                <select
                  value={editedColumn.root_category_id || ''}
                  onChange={(e) => setEditedColumn({ ...editedColumn, root_category_id: e.target.value, selected_subcategories: [] })}
                  className="w-full p-3 border border-slate-300 rounded-lg"
                >
                  <option value="">-- Select Category --</option>
                  {categories
                    .filter(cat => cat.children && cat.children.length > 0)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name} ({cat.children.length} subcategories)
                      </option>
                    ))}
                </select>
              </div>

              {/* Auto-include subcategories toggle */}
              {rootCategory && (
                <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedColumn.auto_include_subcategories !== false}
                      onChange={(e) => setEditedColumn({ ...editedColumn, auto_include_subcategories: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium text-sm">Auto-include new subcategories</span>
                      <p className="text-xs text-slate-600 mt-0.5">
                        When enabled, new subcategories added to "{rootCategory.name}" will automatically appear in this mega menu
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Subcategory Selection with Labels */}
              {rootCategory && rootCategory.children && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-slate-700">Select Subcategories to Display</label>
                  <p className="text-xs text-slate-500">Uncheck to hide, click "+ Label" to add badges</p>
                  <div className="border border-slate-200 rounded-lg p-3 max-h-96 overflow-y-auto space-y-2">
                    {rootCategory.children.map((child) => {
                      const selectedSubs = editedColumn.selected_subcategories || [];
                      const isSelected = selectedSubs.length === 0 || selectedSubs.includes(child.id);
                      
                      // Get labels for this subcategory
                      const subLabels = (editedColumn.labels || []).filter(l => l.subcategory_id === child.id);
                      
                      return (
                        <div key={child.id} className="border border-slate-100 rounded-lg p-2 hover:bg-slate-50">
                          <div className="flex items-center justify-between gap-2">
                            <label className="flex items-center gap-2 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  let newSelected = [...selectedSubs];
                                  if (newSelected.length === 0) {
                                    newSelected = rootCategory.children.filter(c => c.id !== child.id).map(c => c.id);
                                  } else {
                                    if (e.target.checked) {
                                      newSelected.push(child.id);
                                    } else {
                                      newSelected = newSelected.filter(id => id !== child.id);
                                    }
                                  }
                                  setEditedColumn({...editedColumn, selected_subcategories: newSelected});
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm flex-1">{child.name}</span>
                            </label>
                            
                            <button
                              type="button"
                              onClick={() => {
                                const newLabel = {
                                  id: Date.now().toString(),
                                  subcategory_id: child.id,
                                  subcategory_name: child.name,
                                  text: 'New',
                                  color: '#ef4444',
                                  expires_at: ''
                                };
                                setEditedColumn({
                                  ...editedColumn,
                                  labels: [...(editedColumn.labels || []), newLabel]
                                });
                              }}
                              className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 px-2 py-1 hover:bg-indigo-50 rounded"
                            >
                              <Tag className="w-3 h-3" />
                              + Label
                            </button>
                          </div>
                          
                          {/* Labels for this subcategory */}
                          {subLabels.length > 0 && (
                            <div className="mt-2 space-y-2 pl-6">
                              {subLabels.map((label, labelIndex) => (
                                <div key={label.id} className="flex gap-2 items-start bg-white border border-slate-200 rounded p-2">
                                  <div className="flex-1 space-y-2">
                                    <div className="flex gap-2 items-center">
                                      <input
                                        type="text"
                                        placeholder="Badge text"
                                        value={label.text || ''}
                                        onChange={(e) => {
                                          const labels = [...editedColumn.labels];
                                          const idx = labels.findIndex(l => l.id === label.id);
                                          labels[idx].text = e.target.value;
                                          setEditedColumn({...editedColumn, labels});
                                        }}
                                        className="flex-1 p-1 text-xs border border-slate-300 rounded"
                                      />
                                      <input
                                        type="date"
                                        placeholder="Expires"
                                        value={label.expires_at || ''}
                                        onChange={(e) => {
                                          const labels = [...editedColumn.labels];
                                          const idx = labels.findIndex(l => l.id === label.id);
                                          labels[idx].expires_at = e.target.value;
                                          setEditedColumn({...editedColumn, labels});
                                        }}
                                        className="w-32 p-1 text-xs border border-slate-300 rounded"
                                      />
                                      <button
                                        onClick={() => {
                                          const labels = editedColumn.labels.filter(l => l.id !== label.id);
                                          setEditedColumn({...editedColumn, labels});
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                    
                                    <div className="flex gap-2 items-center">
                                      <label className="flex items-center gap-1 text-xs text-slate-600">
                                        <input
                                          type="checkbox"
                                          checked={label.use_default_color !== false}
                                          onChange={(e) => {
                                            const labels = [...editedColumn.labels];
                                            const idx = labels.findIndex(l => l.id === label.id);
                                            labels[idx].use_default_color = e.target.checked;
                                            if (e.target.checked) {
                                              labels[idx].color = menuConfig?.default_label_color || '#ef4444';
                                            }
                                            setEditedColumn({...editedColumn, labels});
                                          }}
                                          className="rounded border-slate-300"
                                        />
                                        Use default color
                                      </label>
                                      {label.use_default_color !== false ? (
                                        <div 
                                          className="w-20 h-6 border border-slate-300 rounded flex items-center justify-center text-xs"
                                          style={{backgroundColor: menuConfig?.default_label_color || '#ef4444', color: '#ffffff'}}
                                        >
                                          Default
                                        </div>
                                      ) : (
                                        <input
                                          type="color"
                                          value={label.color || '#ef4444'}
                                          onChange={(e) => {
                                            const labels = [...editedColumn.labels];
                                            const idx = labels.findIndex(l => l.id === label.id);
                                            labels[idx].color = e.target.value;
                                            setEditedColumn({...editedColumn, labels});
                                          }}
                                          className="w-20 h-6 border border-slate-300 rounded"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700">Additional Items</label>
                  <button
                    type="button"
                    onClick={addCustomItem}
                    className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>
                <p className="text-xs text-slate-500">Add items from other categories or custom links</p>
                {(editedColumn.custom_items || []).length > 0 && (
                  <div className="space-y-3">
                    {editedColumn.custom_items.map((item, index) => (
                      <div key={item.id} className="border border-slate-200 rounded-lg p-3 space-y-3">
                        <div className="flex gap-2 items-start">
                          <div className="flex-1 space-y-2">
                            {/* Category Selector - Hidden for Label type */}
                            {item.display_as !== 'label' && (
                              <div>
                                <label className="block text-xs text-slate-600 mb-1">Link to Category (optional)</label>
                                <select
                                  value={item.category_id || ''}
                                  onChange={(e) => {
                                    const items = [...editedColumn.custom_items];
                                    const selectedCat = categories.find(c => c.id === e.target.value);
                                    items[index].category_id = e.target.value;
                                    if (selectedCat && !items[index].label) {
                                      items[index].label = selectedCat.name;
                                    }
                                    if (selectedCat) {
                                      items[index].url = `/category/${selectedCat.id}`;
                                    }
                                    setEditedColumn({...editedColumn, custom_items: items});
                                  }}
                                  className="w-full p-2 text-sm border border-slate-300 rounded"
                                >
                                  <option value="">Select a category...</option>
                                  {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            
                            <input
                              type="text"
                              placeholder="Item label"
                              value={item.label || ''}
                              onChange={(e) => {
                                const items = [...editedColumn.custom_items];
                                items[index].label = e.target.value;
                                setEditedColumn({...editedColumn, custom_items: items});
                              }}
                              className="w-full p-2 text-sm border border-slate-300 rounded"
                            />
                            
                            {/* URL field - Hidden for Label type */}
                            {item.display_as !== 'label' && (
                              <input
                                type="text"
                                placeholder="URL"
                                value={item.url || ''}
                                onChange={(e) => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].url = e.target.value;
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className="w-full p-2 text-sm border border-slate-300 rounded"
                              />
                            )}
                          </div>
                          <button
                            onClick={() => {
                              const items = editedColumn.custom_items.filter((_, i) => i !== index);
                              setEditedColumn({...editedColumn, custom_items: items});
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Display Options */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">Display As</label>
                            <div className="grid grid-cols-3 gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].display_as = 'link';
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className={`px-2 py-1 text-xs border rounded ${
                                  (item.display_as || 'link') === 'link'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                Link
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].display_as = 'button';
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className={`px-2 py-1 text-xs border rounded ${
                                  item.display_as === 'button'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                Button
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].display_as = 'label';
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className={`px-2 py-1 text-xs border rounded ${
                                  item.display_as === 'label'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                Label
                              </button>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">Position</label>
                            <div className="grid grid-cols-2 gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].position = 'inline';
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className={`px-2 py-1 text-xs border rounded ${
                                  (item.position || 'inline') === 'inline'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                Inline
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].position = 'bottom';
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className={`px-2 py-1 text-xs border rounded ${
                                  item.position === 'bottom'
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                Bottom
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Italics toggle for Label type */}
                        {item.display_as === 'label' && (
                          <div className="pt-2 border-t border-slate-100">
                            <label className="flex items-center gap-2 text-xs text-slate-600">
                              <input
                                type="checkbox"
                                checked={item.italic || false}
                                onChange={(e) => {
                                  const items = [...editedColumn.custom_items];
                                  items[index].italic = e.target.checked;
                                  setEditedColumn({...editedColumn, custom_items: items});
                                }}
                                className="rounded border-slate-300"
                              />
                              Italic text
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Custom Card Config */}
          {editedColumn.type === 'card' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Card Title</label>
                <input
                  type="text"
                  value={editedColumn.title || ''}
                  onChange={(e) => setEditedColumn({ ...editedColumn, title: e.target.value })}
                  placeholder="e.g., Featured Products"
                  className="w-full p-3 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={editedColumn.description || ''}
                  onChange={(e) => setEditedColumn({ ...editedColumn, description: e.target.value })}
                  placeholder="Brief description"
                  rows={3}
                  className="w-full p-3 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Image URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editedColumn.image || ''}
                    onChange={(e) => setEditedColumn({ ...editedColumn, image: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 p-3 border border-slate-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (onBrowseMedia) {
                        onBrowseMedia((url) => {
                          setEditedColumn({ ...editedColumn, image: url });
                        });
                      }
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                  >
                    <Image className="w-4 h-4" />
                    Browse
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Button Text</label>
                  <input
                    type="text"
                    value={editedColumn.button_text || ''}
                    onChange={(e) => setEditedColumn({ ...editedColumn, button_text: e.target.value })}
                    placeholder="Shop Now"
                    className="w-full p-3 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Button URL</label>
                  <div className="space-y-2">
                    <select
                      value={editedColumn.button_category_id || ''}
                      onChange={(e) => {
                        const selectedCat = allCategories.find(c => c.id === e.target.value);
                        if (selectedCat) {
                          // Generate proper category URL using slug
                          const rootDomain = menuConfig?.root_domain || '';
                          const slug = selectedCat.slug || selectedCat.name.toLowerCase().replace(/\s+/g, '-');
                          const categoryUrl = selectedCat.url || (rootDomain ? `${rootDomain.replace(/\/$/, '')}/products-list/collections/${slug}` : `/products-list/collections/${slug}`);
                          setEditedColumn({ 
                            ...editedColumn, 
                            button_category_id: e.target.value,
                            button_url: categoryUrl
                          });
                        } else {
                          setEditedColumn({ 
                            ...editedColumn, 
                            button_category_id: '',
                            button_url: ''
                          });
                        }
                      }}
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="">Choose category or enter URL...</option>
                      {allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {'\u00A0'.repeat(cat.level * 4)}{cat.level > 0 ? '↳ ' : ''}{cat.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={editedColumn.button_url || ''}
                      onChange={(e) => setEditedColumn({ ...editedColumn, button_url: e.target.value })}
                      placeholder="Or enter custom URL..."
                      className="w-full p-3 border border-slate-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            Save Column
          </button>
        </div>
      </div>
    </div>
  );
};

export default MegaMenuColumnEditor;
